import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import {
  getAdminConsumption,
  type ConsumptionStats,
} from "@/lib/admin-consumption.functions";

export const Route = createFileRoute("/conso/")({
  head: () => ({
    meta: [
      { title: "Consommation — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ConsoPage,
});

function fmtBytes(n: number): string {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log10(n) / 3));
  return `${(n / Math.pow(1000, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

function fmtNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function StatCard({
  title,
  stats,
  format = fmtNum,
  hint,
}: {
  title: string;
  stats: ConsumptionStats;
  format?: (n: number) => string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-foreground/10 bg-card p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {title}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
        <div>
          <div className="text-[10px] text-muted-foreground">Total</div>
          <div className="font-bold text-base">{format(stats.sum)}</div>
        </div>
        <div>
          <div className="text-[10px] text-muted-foreground">Moyenne</div>
          <div className="font-bold text-base">{format(stats.avg)}</div>
        </div>
        <div>
          <div className="text-[10px] text-muted-foreground">Maximum</div>
          <div className="font-bold text-base">{format(stats.max)}</div>
          {stats.max_user && (
            <div className="text-[10px] text-muted-foreground truncate">
              {stats.max_user}
            </div>
          )}
        </div>
      </div>
      {hint && (
        <div className="text-[10px] text-muted-foreground mt-2">{hint}</div>
      )}
    </div>
  );
}

function ConsoPage() {
  const nav = useNavigate();
  const fn = useServerFn(getAdminConsumption);
  const [authed, setAuthed] = useState(false);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<
    "ai" | "analyses30" | "preflight" | "storage" | "rows"
  >("ai");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else setAuthed(true);
    });
  }, [nav]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-consumption"],
    queryFn: () => fn(),
    enabled: authed,
  });

  const rows = useMemo(() => {
    if (!data?.users) return [];
    const q = filter.trim().toLowerCase();
    const filtered = q
      ? data.users.filter(
          (u) =>
            (u.email ?? "").toLowerCase().includes(q) ||
            u.user_id.toLowerCase().includes(q),
        )
      : data.users;
    const key =
      sort === "ai"
        ? "ai_calls_total"
        : sort === "analyses30"
          ? "analyses_30d"
          : sort === "preflight"
            ? "pro_preflight_30d"
            : sort === "storage"
              ? "storage_bytes"
              : "db_rows";
    return [...filtered].sort(
      (a, b) => Number(b[key as keyof typeof b]) - Number(a[key as keyof typeof a]),
    );
  }, [data, filter, sort]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1
              className="font-display font-bold text-3xl tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Consommation IA & Cloud
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vue admin : volume d'appels IA et empreinte cloud par utilisateur,
              moyenne et maximum.
            </p>
          </div>
          <div className="flex gap-2">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrer (email, user id…)"
              className="px-3 py-2 rounded-xl border border-foreground/15 bg-card text-sm w-72 max-w-full"
            />
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="bg-foreground text-background px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isFetching ? "…" : "Rafraîchir"}
            </button>
          </div>
        </div>

        {isLoading && (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        )}
        {error && (
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : "Erreur"}
          </p>
        )}

        {data && !data.is_admin && (
          <div className="bg-card border border-foreground/10 rounded-2xl p-6">
            <p className="font-semibold mb-2">Accès refusé</p>
            <p className="text-sm text-muted-foreground">
              Votre compte n'est pas dans la liste des administrateurs. Ajoutez
              votre email au secret serveur <code>ADMIN_EMAILS</code>.
            </p>
            {data.errors.length > 0 && (
              <ul className="mt-3 text-xs text-muted-foreground list-disc list-inside">
                {data.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {data?.is_admin && (
          <>
            {data.errors.length > 0 && (
              <div className="mb-4 p-3 rounded-xl border border-yellow-500/40 bg-yellow-500/5 text-xs">
                {data.errors.map((e, i) => (
                  <p key={i}>⚠️ {e}</p>
                ))}
              </div>
            )}

            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              <StatCard
                title="Appels IA (total)"
                stats={data.stats.ai_calls_total}
                hint="Analyses + observations protocoles + preflight Pro"
              />
              <StatCard
                title="Analyses (30 j)"
                stats={data.stats.analyses_30d}
              />
              <StatCard
                title="Preflight Pro (30 j)"
                stats={data.stats.pro_preflight_30d}
              />
              <StatCard
                title="Stockage selfies"
                stats={data.stats.storage_bytes}
                format={fmtBytes}
              />
              <StatCard
                title="Fichiers selfies"
                stats={data.stats.selfies_count}
              />
              <StatCard
                title="Lignes DB (analyses + protocoles)"
                stats={data.stats.db_rows}
              />
            </section>

            <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
              <span className="text-muted-foreground">Trier par&nbsp;:</span>
              {(
                [
                  ["ai", "Appels IA"],
                  ["analyses30", "Analyses 30j"],
                  ["preflight", "Preflight 30j"],
                  ["storage", "Stockage"],
                  ["rows", "Lignes DB"],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setSort(k)}
                  className={`px-2 py-1 rounded-full border ${
                    sort === k
                      ? "bg-foreground text-background border-foreground"
                      : "border-foreground/15 hover:bg-muted"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <section>
              <div className="overflow-x-auto border border-foreground/10 rounded-2xl">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3">Utilisateur</th>
                      <th className="p-3 text-right">Appels IA</th>
                      <th className="p-3 text-right">Analyses (tot.)</th>
                      <th className="p-3 text-right">Analyses 30j</th>
                      <th className="p-3 text-right">Obs. protocoles</th>
                      <th className="p-3 text-right">Preflight 30j</th>
                      <th className="p-3 text-right">Selfies</th>
                      <th className="p-3 text-right">Stockage</th>
                      <th className="p-3 text-right">Lignes DB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((u) => (
                      <tr
                        key={u.user_id}
                        className="border-t border-foreground/10 align-top"
                      >
                        <td className="p-3">
                          <div className="font-medium">{u.email ?? "—"}</div>
                          <div className="text-muted-foreground font-mono text-[10px]">
                            {u.user_id.slice(0, 8)}…
                          </div>
                        </td>
                        <td className="p-3 text-right font-bold">
                          {fmtNum(u.ai_calls_total)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtNum(u.analyses_total)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtNum(u.analyses_30d)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtNum(u.protocol_observations_total)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtNum(u.pro_preflight_30d)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtNum(u.selfies_count)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtBytes(u.storage_bytes)}
                        </td>
                        <td className="p-3 text-right">
                          {fmtNum(u.db_rows)}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="p-6 text-center text-muted-foreground"
                        >
                          Aucun utilisateur.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Généré le {new Date(data.generated_at).toLocaleString()}. Les
                « appels IA » sont une estimation basée sur les analyses,
                observations de protocoles et preflights Pro (30 j) — proxy de
                la consommation Lovable AI Gateway. Le « stockage » mesure le
                bucket <code>selfies</code> ; les autres usages cloud
                (bande passante, edge, Postgres) sont consultables dans
                Lovable Cloud.
              </p>
            </section>
          </>
        )}

        <Link
          to="/billing"
          className="block text-center text-sm text-muted-foreground hover:text-foreground mt-10"
        >
          ← Retour
        </Link>
      </div>
    </div>
  );
}
